import {
  calculatePurchaserDeliveryCost,
  calculatePurchaserNetSubtotal,
  calculatePurchaserTotalAmount,
} from "@fero/utils";
import type { BigDecimal } from "adl-gen/common";
import type {
  Currency,
  DeliveryFee,
  DutyAndVat,
  PurchaseRequestSalePriceType,
  PurchaseRequestState,
} from "adl-gen/ferovinum/app/db";
import type {
  AdditionalCost,
  PurchaseRequestLineItem,
} from "adl-gen/ferovinum/app/nompurchaser";
import { PurchaserPurchaseRequestDetailsInnerView } from "../../../page/purchaser/purchaser-purchase-request-details/purchaser-purchase-request-details-page-view";

export interface OrganisationPurchaserSummarySectionProps {
  purchaseRequestData: {
    state: PurchaseRequestState;
    purchaserCurrency: Currency;
    settlementCurrency: Currency;
    summary: {
      netSubtotal: string;
      dealDiscountAmount: BigDecimal | null;
      dutyAndVat: DutyAndVat | null;
      deliveryFee: DeliveryFee | null;
      customDeliveryCost: AdditionalCost | null;
      additionalDeliveryCost: AdditionalCost | null;
      lineItems: Pick<
        PurchaseRequestLineItem,
        | "product"
        | "paidUnits"
        | "freeUnits"
        | "salePricePerPaidUnit"
        | "priceDiscount"
        | "attributeSelection"
      >[];
      dutyAndVatInPurchaserCurrency: DutyAndVat | null;
      deliveryFeeInPurchaserCurrency: DeliveryFee | null;
    };
    salePriceType: PurchaseRequestSalePriceType;
    purchaserCoversDeliveryCost: boolean;
  };
}

export const OrganisationPurchaserSummarySection = ({
  purchaseRequestData,
}: OrganisationPurchaserSummarySectionProps) => {
  const { summary, salePriceType, purchaserCoversDeliveryCost } =
    purchaseRequestData;

  const purchaserNetSubtotal = calculatePurchaserNetSubtotal(
    salePriceType,
    summary.netSubtotal,
    summary.dutyAndVatInPurchaserCurrency || null,
  );

  const purchaserDeliveryCost = calculatePurchaserDeliveryCost(
    summary.deliveryFeeInPurchaserCurrency,
    summary.customDeliveryCost,
    summary.additionalDeliveryCost,
    purchaserCoversDeliveryCost,
  );

  const purchaserTotal = calculatePurchaserTotalAmount(
    summary.netSubtotal,
    summary.dutyAndVatInPurchaserCurrency || null,
    purchaserDeliveryCost,
    purchaserCoversDeliveryCost,
  );

  return (
    <PurchaserPurchaseRequestDetailsInnerView
      purchaseRequestData={{
        ...purchaseRequestData,
        breakdown: {
          ...purchaseRequestData.summary,
          deliveryFeeInPurchaserCurrency: purchaserDeliveryCost,
          netSubtotal: purchaserNetSubtotal,
          totalPayable: purchaserTotal,
          lineItems: purchaseRequestData.summary.lineItems.map((li) => ({
            product: li.product,
            paidUnits: li.paidUnits,
            freeUnits: li.freeUnits,
            priceDiscount: li.priceDiscount,
            unitPrice: li.salePricePerPaidUnit,
            subTotal: String(
              Number(li.paidUnits.value) * Number(li.salePricePerPaidUnit),
            ),
            attributeSelection: li.attributeSelection,
          })),
        },
      }}
    />
  );
};
